import React from 'react';

const CONTACT_EMAIL = 'rain@elevenup.xyz';
const LAST_UPDATED = '10 February 2025';

const PrivacyPolicy: React.FC = () => {
  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <nav style={styles.breadcrumb}>
          <a href="/" style={styles.breadcrumbLink}>Home</a>
          <span style={styles.breadcrumbSep}>/</span>
          <span style={styles.breadcrumbCurrent}>Privacy Policy</span>
        </nav>

        <h1 style={styles.title}>Privacy Policy</h1>
        <p style={styles.lastUpdated}>Last updated: {LAST_UPDATED}</p>

        {/* ── For the Students ── */}
        <div style={styles.kidsBox}>
          <h2 style={styles.kidsTitle}>For the Students</h2>
          <p style={styles.kidsParagraph}>
            Hey! Before you get started, here's what you should know about how ElevenUp uses your information. We've kept it simple:
          </p>
          <ul style={styles.kidsList}>
            <li><strong>Your first name</strong> — so Eli (your AI tutor) can say "Well done!" to YOU, not just anyone.</li>
            <li><strong>Your scores and streaks</strong> — so we can show you how awesome you're doing and what to practise next.</li>
            <li><strong>Questions you ask Eli</strong> — Eli reads your question, answers it, and then forgets it straight away. Nobody else sees it.</li>
          </ul>
          <p style={styles.kidsParagraph}>
            That's it! We don't know what school you go to, where you live, how old you are, or what you look like. We don't show you any adverts. And we never share your stuff with anyone else.
          </p>
          <p style={styles.kidsParagraph}>
            If you ever want us to delete everything, just ask your parent to email us and we'll sort it out.
          </p>
        </div>

        {/* ── Plain English Summary (for parents) ── */}
        <div style={styles.summaryBox}>
          <h2 style={styles.summaryTitle}>For parents — in plain English</h2>
          <ul style={styles.summaryList}>
            <li>We collect your child's <strong>first name</strong> and your <strong>email address</strong> — that's it for personal info.</li>
            <li>We track <strong>learning progress</strong> (quiz scores, streaks, badges) so the app works properly.</li>
            <li>Your child can ask our AI tutor questions — these are processed by OpenAI but <strong>never stored or used for training</strong>.</li>
            <li>We <strong>don't use any tracking cookies, analytics, or advertising</strong>.</li>
            <li>We <strong>never sell or share</strong> your data with anyone.</li>
            <li>You can ask us to <strong>delete everything</strong> at any time.</li>
          </ul>
        </div>

        {/* ── Full Policy ── */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Who We Are</h2>
          <p style={styles.paragraph}>
            ElevenUp is operated by 11 Up Company Ltd ("we", "us", "our"). We provide an educational app to help children prepare for 11+ grammar school entrance exams in the United Kingdom.
          </p>
          <p style={styles.paragraph}>
            For privacy queries, contact us at: <a href={`mailto:${CONTACT_EMAIL}`} style={styles.link}>{CONTACT_EMAIL}</a>
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>2. Data We Collect</h2>

          <h3 style={styles.subTitle}>Information you provide</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Data</th>
                <th style={styles.th}>Why we need it</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.td}>Child's first name</td>
                <td style={styles.td}>To personalise the app experience ("Well done, Alex!")</td>
              </tr>
              <tr>
                <td style={styles.td}>Parent/guardian email</td>
                <td style={styles.td}>Account login, parental consent verification, and password reset</td>
              </tr>
              <tr>
                <td style={styles.td}>Password</td>
                <td style={styles.td}>Account security (stored securely hashed — we never see your actual password)</td>
              </tr>
              <tr>
                <td style={styles.td}>Learning preferences</td>
                <td style={styles.td}>Preferred subjects and learning goals to personalise the experience</td>
              </tr>
            </tbody>
          </table>

          <h3 style={styles.subTitle}>Information collected automatically</h3>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Data</th>
                <th style={styles.th}>Why we need it</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={styles.td}>Quiz scores and progress</td>
                <td style={styles.td}>Track learning and adapt difficulty</td>
              </tr>
              <tr>
                <td style={styles.td}>Streaks, badges, and XP</td>
                <td style={styles.td}>Gamification features to keep learning fun</td>
              </tr>
              <tr>
                <td style={styles.td}>Daily activity data</td>
                <td style={styles.td}>Study goal tracking and progress dashboard</td>
              </tr>
              <tr>
                <td style={styles.td}>Mock exam results</td>
                <td style={styles.td}>Performance tracking over time</td>
              </tr>
            </tbody>
          </table>

          <h3 style={styles.subTitle}>What we do NOT collect</h3>
          <p style={styles.paragraph}>
            We do not collect: age, date of birth, full name, school name, home address, phone number, location data, photos, or any other personal information beyond what is listed above.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>3. AI Tutor ("Eli")</h2>
          <p style={styles.paragraph}>
            Our app includes an AI tutor called Eli, powered by OpenAI's API. When your child asks Eli a question:
          </p>
          <ul style={styles.list}>
            <li>The question is sent to OpenAI's API for processing.</li>
            <li>OpenAI's API does <strong>not</strong> use API data to train its models (this is OpenAI's default policy for API usage).</li>
            <li>We do <strong>not</strong> store conversation history — questions and answers are not saved after the session.</li>
            <li>Eli is programmed to only discuss 11+ exam subjects (Maths, English, Verbal Reasoning). It will politely redirect any off-topic questions.</li>
            <li>All inputs are checked by a content moderation system before being processed.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>4. How We Store Your Data</h2>
          <ul style={styles.list}>
            <li><strong>Cloud database:</strong> Your account and progress data is stored in Supabase, hosted in the EU (Ireland). Data is encrypted in transit and at rest.</li>
            <li><strong>On your device:</strong> Progress is also cached locally on your device using standard browser storage, so the app works quickly and can function offline.</li>
            <li><strong>Guest mode:</strong> If you use the app without creating an account, all data stays on your device only — nothing is sent to our servers.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>5. Payment Data</h2>
          <p style={styles.paragraph}>
            If you subscribe to a paid plan, payments are processed entirely by Stripe. We never see or store your card number, bank details, or other payment information. We only store a Stripe customer ID so we can manage your subscription.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>6. Cookies and Tracking</h2>
          <p style={styles.paragraph}>
            We do <strong>not</strong> use:
          </p>
          <ul style={styles.list}>
            <li>Advertising cookies or tracking pixels</li>
            <li>Third-party analytics (no Google Analytics, no Mixpanel, nothing)</li>
            <li>Social media tracking</li>
            <li>Any form of behavioural profiling</li>
          </ul>
          <p style={styles.paragraph}>
            The only cookie-like storage we use is a secure authentication token (essential for keeping you logged in) and local progress data. Both are strictly necessary for the app to function.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>7. Data Sharing</h2>
          <p style={styles.paragraph}>
            We do <strong>not</strong> sell, rent, or share your personal data with any third party for marketing or advertising purposes. The only third parties that process data on our behalf are:
          </p>
          <ul style={styles.list}>
            <li><strong>Supabase</strong> — database hosting and authentication (EU-based)</li>
            <li><strong>OpenAI</strong> — AI tutor responses (API data not used for training)</li>
            <li><strong>Stripe</strong> — payment processing (only if you subscribe to a paid plan)</li>
            <li><strong>Vercel</strong> — website and app hosting</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>8. Children's Privacy</h2>
          <p style={styles.paragraph}>
            ElevenUp is designed for children preparing for 11+ exams. We take children's privacy very seriously:
          </p>
          <ul style={styles.list}>
            <li>Accounts must be created by a parent or guardian.</li>
            <li>We require verifiable parental consent before processing any child's data.</li>
            <li>We collect the minimum data necessary (first name only — not full name, not age, not school).</li>
            <li>We do not display advertising to children.</li>
            <li>We do not use data for profiling or behavioural targeting.</li>
            <li>Parents can review, access, or delete their child's data at any time.</li>
          </ul>
          <p style={styles.paragraph}>
            We comply with the UK General Data Protection Regulation (UK GDPR), the UK Age Appropriate Design Code (Children's Code), and the US Children's Online Privacy Protection Act (COPPA).
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>9. Your Rights</h2>
          <p style={styles.paragraph}>
            As a parent or guardian, you have the right to:
          </p>
          <ul style={styles.list}>
            <li><strong>Access</strong> — request a copy of all data we hold about your child</li>
            <li><strong>Correction</strong> — ask us to correct any inaccurate data</li>
            <li><strong>Deletion</strong> — ask us to delete your child's account and all associated data</li>
            <li><strong>Withdraw consent</strong> — withdraw your parental consent at any time, after which we will stop processing and delete the data</li>
            <li><strong>Portability</strong> — request your data in a machine-readable format</li>
            <li><strong>Complaint</strong> — lodge a complaint with the UK Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" style={styles.link} target="_blank" rel="noopener noreferrer">ico.org.uk</a></li>
          </ul>
          <p style={styles.paragraph}>
            To exercise any of these rights, email us at <a href={`mailto:${CONTACT_EMAIL}`} style={styles.link}>{CONTACT_EMAIL}</a>. We will respond within 30 days.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>10. Data Retention</h2>
          <p style={styles.paragraph}>
            We keep your data for as long as your account is active. If you ask us to delete your account, we will delete all personal data within 30 days. Anonymised, aggregated data (e.g. "50 users completed this quiz") may be retained for service improvement.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>11. Security</h2>
          <p style={styles.paragraph}>
            We use industry-standard security measures including:
          </p>
          <ul style={styles.list}>
            <li>HTTPS encryption for all data in transit</li>
            <li>Encrypted database storage (AES-256)</li>
            <li>Secure password hashing (bcrypt via Supabase Auth)</li>
            <li>Row-level security policies ensuring users can only access their own data</li>
            <li>No sensitive data stored in browser cookies or URL parameters</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>12. Changes to This Policy</h2>
          <p style={styles.paragraph}>
            If we make significant changes to this privacy policy, we will notify you by email before the changes take effect. Minor clarifications may be made without notice. The "last updated" date at the top of this page will always reflect the most recent version.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>13. Contact Us</h2>
          <p style={styles.paragraph}>
            If you have any questions about this privacy policy or how we handle your data, please contact us:
          </p>
          <p style={styles.paragraph}>
            <strong>Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`} style={styles.link}>{CONTACT_EMAIL}</a><br />
            <strong>Organisation:</strong> 11 Up Company Ltd
          </p>
        </section>

        <div style={styles.footer}>
          <p style={styles.footerText}>© {new Date().getFullYear()} 11 Up Company Ltd. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0f0f23',
    color: '#e0e0e0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  content: {
    maxWidth: 720,
    margin: '0 auto',
    padding: '40px 24px 80px',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: '#4FCCC6',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '8px 0',
    marginBottom: 24,
  },
  breadcrumb: {
    marginBottom: 24,
    fontSize: 14,
  },
  breadcrumbLink: {
    color: '#4FCCC6',
    textDecoration: 'none',
    fontWeight: 500,
  },
  breadcrumbSep: {
    margin: '0 8px',
    color: '#666',
  },
  breadcrumbCurrent: {
    color: '#999',
  },
  title: {
    fontSize: 36,
    fontWeight: 800,
    color: '#ffffff',
    marginBottom: 8,
  },
  lastUpdated: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.4)',
    marginBottom: 32,
  },
  kidsBox: {
    backgroundColor: 'rgba(255, 215, 0, 0.08)',
    border: '1px solid rgba(255, 215, 0, 0.2)',
    borderRadius: 16,
    padding: '24px 28px',
    marginBottom: 24,
  },
  kidsTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#FFD700',
    marginTop: 0,
    marginBottom: 12,
  },
  kidsParagraph: {
    fontSize: 15,
    lineHeight: 1.8,
    color: 'rgba(255,255,255,0.85)',
    marginBottom: 12,
    marginTop: 0,
  },
  kidsList: {
    margin: '0 0 12px 0',
    paddingLeft: 20,
    lineHeight: 1.9,
    fontSize: 15,
    color: 'rgba(255,255,255,0.85)',
  },
  summaryBox: {
    backgroundColor: 'rgba(79, 204, 198, 0.08)',
    border: '1px solid rgba(79, 204, 198, 0.2)',
    borderRadius: 16,
    padding: '24px 28px',
    marginBottom: 40,
  },
  summaryTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#4FCCC6',
    marginTop: 0,
    marginBottom: 16,
  },
  summaryList: {
    margin: 0,
    paddingLeft: 20,
    lineHeight: 1.8,
    fontSize: 15,
    color: 'rgba(255,255,255,0.85)',
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: 12,
    marginTop: 0,
  },
  subTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.8)',
    marginBottom: 8,
    marginTop: 16,
  },
  paragraph: {
    fontSize: 15,
    lineHeight: 1.7,
    color: 'rgba(255,255,255,0.75)',
    marginBottom: 12,
  },
  list: {
    paddingLeft: 20,
    lineHeight: 1.8,
    fontSize: 15,
    color: 'rgba(255,255,255,0.75)',
    marginBottom: 12,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginBottom: 16,
    fontSize: 14,
  },
  th: {
    textAlign: 'left' as const,
    padding: '10px 14px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.6)',
    fontWeight: 600,
    fontSize: 13,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
  td: {
    padding: '10px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    color: 'rgba(255,255,255,0.75)',
    verticalAlign: 'top' as const,
  },
  link: {
    color: '#4FCCC6',
    textDecoration: 'none',
  },
  footer: {
    marginTop: 48,
    paddingTop: 24,
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  footerText: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.3)',
    textAlign: 'center' as const,
  },
};

export default PrivacyPolicy;
